import React from 'react';
import type { RescueUnassignedInventoryDeviceItemGql } from '../../lib/rescueQueries';
import UnassignedDeviceRow from './UnassignedDeviceRow';
import { SkeletonRows } from './SkeletonRow';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import styles from './Devices.module.scss';

const columns = ['Device', 'Status', 'Battery', 'Action'];

interface StockTableProps {
  /** Devices for the current page (already sliced by the caller's client pagination). */
  devices: RescueUnassignedInventoryDeviceItemGql[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  hasError: boolean;
  onRetry: () => void;
  onAssign: (moduleId: string) => void;
  searchQuery: string;
}

/** Delivered devices not yet attached to a pet (formerly the "Unassigned" group). */
export default function StockTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  hasError,
  onRetry,
  onAssign,
  searchQuery,
}: StockTableProps) {
  const colCount = columns.length;
  const showSkeleton = loading && totalCount === 0 && !hasError;

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Stock ({totalCount})</h3>
        <p className={styles.sectionDescription}>Devices delivered to your rescue and not yet attached to a pet.</p>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '32%' }} /> {/* Device */}
            <col style={{ width: '24%' }} /> {/* Status */}
            <col style={{ width: '16%' }} /> {/* Battery */}
            <col style={{ width: '28%' }} /> {/* Action */}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hasError ? (
              <tr>
                <td colSpan={colCount} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load inventory.</span>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={5} columns={colCount} />
            ) : totalCount === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No stock devices matching "${searchQuery}"` : 'No devices in stock'}
                </td>
              </tr>
            ) : (
              devices.map((d) => <UnassignedDeviceRow key={d.moduleId} device={d} onAssign={onAssign} />)
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}
